const DEFAULT_API_DOMAIN = 'https://devportal.softcom.ua';

function resolveApiDomain(): string {
  const v = import.meta.env.VITE_API_DOMAIN;
  if (typeof v === 'string' && v.trim() !== '') {
    return v.trim();
  }
  return DEFAULT_API_DOMAIN;
}

/** PHP API origin (`VITE_API_DOMAIN` in `.env*` / CI env; fallback dev host). */
export const domain: string = resolveApiDomain();

/** Failed HTTP response from the PHP API (fetch layer). */
export class ApiHttpError extends Error {
  readonly status: number;
  readonly bodySnippet: string;

  constructor(status: number, bodySnippet: string) {
    super(`HTTP ${status}: ${bodySnippet}`);
    this.name = 'ApiHttpError';
    this.status = status;
    this.bodySnippet = bodySnippet;
  }
}

/** Absolute URL under {@link domain}. `path` must start with `/` (e.g. `/php/order.php`). */
export function apiUrl(path: string): string {
  if (!path.startsWith('/')) {
    throw new Error(`apiUrl: path must start with /, got: ${path}`);
  }
  return `${domain}${path}`;
}

/** Throw {@link ApiHttpError} when `!response.ok`. */
export async function assertResponseOk(response: Response): Promise<void> {
  if (!response.ok) {
    const text = await response.text();
    throw new ApiHttpError(response.status, text.slice(0, 200));
  }
}

/** After `assertResponseOk`, require non-empty JSON body. */
export async function parseJsonOrThrow<T>(response: Response): Promise<T> {
  await assertResponseOk(response);
  const text = await response.text();
  if (!text.trim()) {
    throw new Error('Empty response body');
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(`Invalid JSON: ${text.slice(0, 200)}`);
  }
}

/**
 * `newsadd.php` may return JSON, a plain `"ok"` string, or non-JSON with "ok" in body.
 * Shares `assertResponseOk` with `parseJsonOrThrow`.
 */
export async function parseNewsAddResponse(
  response: Response
): Promise<unknown> {
  await assertResponseOk(response);
  const text = await response.text();
  try {
    const parsed = JSON.parse(text) as unknown;
    if (typeof parsed === 'string' && /ok/i.test(parsed.trim())) {
      return { status: 'add ok' };
    }
    return parsed;
  } catch {
    if (/ok/i.test(text.trim())) {
      return { status: 'add ok' };
    }
    throw new Error(`Invalid JSON: ${text.slice(0, 100)}`);
  }
}
